import { MarketValueInputs, ProjectedFigures } from "../types";

/**
 * Calculates the initial revenue for a gene therapy market based on several factors.
 *
 * @param initialPatientPopulation - The estimated number of initial patients eligible for the therapy.
 * @param therapeuticPrice - The target price per therapy treatment.
 * @param marketPenetrationRate - The expected market penetration rate as a percentage (0-100).
 * @param approvalProbability - The probability of regulatory approval as a percentage (0-100).
 * @returns Each value multiplied together, with the percentages being converted to decimals (/100).
 */
export const calculateInitialRevenue = (
  initialPatientPopulation: number,
  therapeuticPrice: number,
  marketPenetrationRate: number,
  approvalProbability: number
) =>
  initialPatientPopulation *
  therapeuticPrice *
  (marketPenetrationRate / 100) *
  (approvalProbability / 100);

/**
 * Calculates the yearly revenue based on the number of new patients,
 * the price per treatment, the market penetration rate, and the
 * probability of regulatory approval.
 *
 * @param newPatients - The number of new patients expected to receive the treatment in a year.
 * @param price - The price of the treatment per patient.
 * @param marketPenetrationRate - The percentage of the target market expected to adopt the treatment (0-100).
 * @param approvalProbability - The probability of regulatory approval for the treatment (0-100).
 * @returns Each value multiplied together, with the percentages being converted to decimals (/100).
 */
const calculateYearlyRevenue = (
  newPatients: number,
  price: number,
  marketPenetrationRate: number,
  approvalProbability: number
): number =>
  newPatients *
  price *
  (marketPenetrationRate / 100) *
  (approvalProbability / 100);

/**
 * Calculates the cumulative revenue by adding the yearly revenue to the previous cumulative revenue.
 *
 * @param previousCumulative - The cumulative revenue from previous years.
 * @param yearlyRevenue - The revenue generated in the current year.
 * @returns The updated cumulative revenue.
 */
const calculateCumulativeRevenue = (
  previousCumulative: number,
  yearlyRevenue: number
): number => {
  return previousCumulative + yearlyRevenue;
};

export const calculateOneYearEconomicImpact = (
  healthcareSavingsPerPatient: number,
  productivityGainsPerPatient: number,
  numberOfPatients: number
): number => {
  return (
    healthcareSavingsPerPatient * numberOfPatients +
    productivityGainsPerPatient * numberOfPatients
  );
};

/**
 * Calculates the projected market value based on the provided inputs.
 *
 * @param inputs - An object containing the following properties:
 *   - `initialPatientPopulation`: The initial number of patients eligible for the therapy.
 *   - `newPatientsPerYear`: The number of new patients added each year.
 *   - `therapeuticPrice`: The price of the therapy per patient.
 *   - `projectionYears`: The number of years to project revenue for.
 *
 * @returns An array of `ProjectedFigures` objects, each containing:
 *   - `year`: The year of the projection (starting from 0).
 *   - `revenue`: The revenue generated in that year.
 *   - `cumulativeRevenue`: The cumulative revenue up to and including that year.
 *   - `presentValue`: The present value of the revenue for that year, discounted to the present.
 */
export const calculateMarketValue = (
  inputs: MarketValueInputs
): ProjectedFigures[] => {
  const {
    initialPatientPopulation,
    newPatientsPerYear,
    therapeuticPrice,
    projectionYears,
    marketPenetrationRate,
    approvalProbability,
  } = inputs;

  // Start with 0 revenue for year 0 and
  // Set Year 1 revenue to account for initial patients only.
  const yearOneRevenue = calculateInitialRevenue(
    initialPatientPopulation,
    therapeuticPrice,
    marketPenetrationRate,
    approvalProbability
  );

  const projections: ProjectedFigures[] = [
    {
      year: 0,
      revenue: 0,
      cumulativeRevenue: 0,
      economicImpact: 0,
    },
    {
      year: 1,
      revenue: yearOneRevenue,
      cumulativeRevenue: yearOneRevenue,
      economicImpact: calculateOneYearEconomicImpact(
        inputs.healthcareCostPerPatient,
        inputs.economicCostPerPatient,
        initialPatientPopulation * (inputs.marketPenetrationRate / 100)
      ),
    },
  ];

  // Add projections for year 2+
  for (let year = 2; year <= projectionYears; year++) {
    // Calculate revenue
    const yearlyRevenue = calculateYearlyRevenue(
      newPatientsPerYear,
      therapeuticPrice,
      marketPenetrationRate,
      approvalProbability
    );

    // Calculate cumulative revenue
    const previousCumulative =
      year > 0 ? projections[year - 1].cumulativeRevenue : 0;
    const cumulativeRevenue = calculateCumulativeRevenue(
      previousCumulative,
      yearlyRevenue
    );

    projections.push({
      year,
      revenue: yearlyRevenue,
      cumulativeRevenue,
      economicImpact: calculateOneYearEconomicImpact(
        inputs.healthcareCostPerPatient,
        inputs.economicCostPerPatient,
        newPatientsPerYear * (inputs.marketPenetrationRate / 100)
      ),
    });
  }

  return projections;
};

/**
 * Calculates the total number of patients over a projection period.
 *
 * @param initialPatientPopulation - The initial number of patients at the start of the projection.
 * @param newPatientsPerYear - The number of new patients added each year.
 * @param projectionYears - The number of years over which the projection is calculated.
 * @returns The total number of patients after the projection period.
 */
export const calculateTotalPatients = (
  initialPatientPopulation: number,
  newPatientsPerYear: number,
  projectionYears: number
): number => {
  return initialPatientPopulation + newPatientsPerYear * projectionYears;
};

/**
 * Calculates the total market value for a gene therapy product by considering
 * projected revenues, development costs, economic costs per patient, and the
 * number of patients.
 *
 * @param projections - An array of projected revenue objects, where each object
 * contains the present value of revenue for a specific year.
 * @param developmentCost - The total cost of developing the gene therapy product.
 * @param economicCostPerPatient - The economic cost incurred per patient.
 * @param numberOfPatients - The total number of patients expected to use the therapy.
 * @returns The calculated total market value as a number.
 */
export const calculateTotalMarketValue = (
  projections: ProjectedFigures[],
  developmentCost: number
): number => {
  const { totalRevenue, economicImpact } = projections.reduce(
    ({ totalRevenue, economicImpact }, currentProjection) => {
      return {
        totalRevenue: totalRevenue + currentProjection.revenue,
        economicImpact: economicImpact + currentProjection.economicImpact,
      };
    },
    { totalRevenue: 0, economicImpact: 0 }
  );

  return totalRevenue + economicImpact - developmentCost;
};

export const calculateRiskAdjustedValue = (
  marketValue: number,
  riskProbability: number
): number => {
  return marketValue * riskProbability;
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("en-US").format(value);
};
